/**
 * NAV ICONS THAT DO NOT RIDE IN THE FIRST-LOAD BUNDLE.
 *
 * WHY (2026-10-22, register mobile-first-paint-1021, owner: "do the nav icons next"). nav.ts listed 68
 * lucide icons as `Icon: Radar` etc., and it is imported by Navbar and, through the page-search index, by
 * GlobalSearch, both of which are on every page. So every first visit downloaded and ran the whole icon
 * set: on the live homepage the lucide chunk was 36 KB gz with 25% of it executed during load. Yet no nav
 * icon is drawn at load on ANY device: Navbar starts with isDesktop=false, so the desktop sidebar mounts
 * only after hydration, the phone drawer only when tapped, and search results only when typing.
 *
 * So each nav item now points at a tiny stand-in (`I.Radar`) that loads navIconSet.ts the first time any
 * menu actually draws, and renders an empty box of the same size until it lands, so nothing shifts. The
 * set is also fetched once the page has settled (after load, when idle), so by the time anyone opens the
 * phone drawer it is normally already there.
 *
 * ⚠ Same component per name for the life of the page (cached), so React never remounts an icon.
 * ⚠ Not a 'use client' module on purpose: nav.ts is also read by node scripts and tests, where a client
 *   reference would not be callable. Only client components render these (Navbar, WatchlistPane,
 *   GlobalSearch), which is where the hooks inside run.
 */
import { createElement, useEffect, useState } from 'react'
import type { LucideIcon, LucideProps } from 'lucide-react'

type IconSet = typeof import('./navIconSet')
export type NavIconName = Exclude<keyof IconSet, 'default'>

let loaded: IconSet | null = null
let loading: Promise<IconSet> | null = null
const loadSet = (): Promise<IconSet> => (loading ??= import('./navIconSet').then((m) => (loaded = m)))

/** Load the icon set now (the page also does this on its own once it has settled). */
export const preloadNavIcons = (): Promise<unknown> => loadSet()

// Warm the set off the critical path: after the page has loaded, when the browser is idle.
if (typeof window !== 'undefined') {
  const warm = () => {
    const ric = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback
    if (ric) ric(() => { void loadSet() }, { timeout: 4000 })
    else setTimeout(() => { void loadSet() }, 1500)
  }
  if (document.readyState === 'complete') warm()
  else window.addEventListener('load', warm, { once: true })
}

const cache = new Map<string, LucideIcon>()

export function navIcon(name: NavIconName): LucideIcon {
  const hit = cache.get(name)
  if (hit) return hit
  function NavIcon(props: LucideProps) {
    const [, rerender] = useState(0)
    useEffect(() => {
      if (!loaded) {
        let live = true
        loadSet().then(() => { if (live) rerender((n) => n + 1) }).catch(() => { /* stays an empty box */ })
        return () => { live = false }
      }
    }, [])
    if (loaded) return createElement(loaded[name] as LucideIcon, props)
    // the icon's own box (its className carries the size), invisible, so the label never jumps
    return createElement('span', { className: props.className, 'aria-hidden': true, style: { display: 'inline-block' } })
  }
  NavIcon.displayName = `NavIcon(${name})`
  const comp = NavIcon as unknown as LucideIcon
  cache.set(name, comp)
  return comp
}

/** `I.Radar` reads like the static import it replaces. */
export const I: Record<NavIconName, LucideIcon> = new Proxy({} as Record<NavIconName, LucideIcon>, {
  get: (_t, name) => navIcon(name as NavIconName),
})
